import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Inbox, Loader2, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * The states every page shares (z8nrz7fv5m): something failed, nothing to
 * show, still loading. One look for each, so a page never invents its own.
 */

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      className="flex items-center gap-3 rounded-lg border border-status-escalated-fg/20 bg-status-escalated-bg px-4 py-3 text-meta text-status-escalated-fg"
    >
      <AlertTriangle className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 grow">{message}</span>
      {onRetry ? (
        <Button size="sm" variant="outline" className="shrink-0 bg-background" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  )
}

export function EmptyState({
  message,
  title,
  icon: Icon = Inbox,
  action,
  compact = false,
}: {
  message: string
  /** A short line above the message, when the message alone does not say what happened. */
  title?: string
  icon?: LucideIcon
  action?: ReactNode
  /** Inside a panel, tab or table rather than filling a page. */
  compact?: boolean
}) {
  return (
    <div className={cn('flex flex-col items-center text-center', compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14')}>
      <span className={cn('flex items-center justify-center rounded-full bg-accent text-primary', compact ? 'size-10' : 'size-12')}>
        <Icon className={compact ? 'size-4' : 'size-5'} aria-hidden />
      </span>
      <div className="flex max-w-sm flex-col gap-1">
        {title ? <p className="text-body font-semibold text-ink">{title}</p> : null}
        <p className="text-body text-ink-muted">{message}</p>
      </div>
      {action}
    </div>
  )
}

export function LoadingState({ label, className }: { label?: string; className?: string }) {
  const { t } = useTranslation()
  return (
    <p role="status" aria-live="polite" className={cn('flex items-center gap-2 py-6 text-meta text-ink-muted', className)}>
      <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
      {label ?? t('common.loading')}
    </p>
  )
}

/** A grey block standing in for content that is on its way. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cn('block animate-pulse rounded bg-surface-subtle motion-reduce:animate-none', className)} />
}

/** Error text for any thrown value. */
export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
